import {
    AxisScale,
    ChartRange,
    FrequencyWaterfallChartSeriesDataPoint,
    Point,
} from '@chart/core/types';
import { Bounds } from '@chart/shared/Bounds';
import { isChartRangeEmpty, mapDataPoint } from '@chart/shared/data';

export const SeriesOffsetLabelVisibilityMinHeightPx = 15;
export const WaterfallChartMinSeriesOffset = 0;
export const WaterfallChartMaxSeriesOffset = 1;
export const WaterfallChartDefaultSeriesOffset = 0.5;

export const WaterfallChartSeriesOffsetRange: ChartRange = {
    min: WaterfallChartMinSeriesOffset,
    max: WaterfallChartMaxSeriesOffset,
};

export type FindClosestPointOptions = Readonly<{
    point: Point;
    firstSeriesBounds: Bounds;
    seriesDataPoints: FrequencyWaterfallChartSeriesDataPoint[];
    leftAxisZoom: ChartRange;
    leftAxisScale: AxisScale;
    bottomAxisZoom: ChartRange;
    bottomAxisScale: AxisScale;
    seriesOffsetPx: number;
}>;

export const findClosestPoint = ({
    point,
    firstSeriesBounds,
    seriesDataPoints,
    leftAxisZoom,
    leftAxisScale,
    bottomAxisZoom,
    bottomAxisScale,
    seriesOffsetPx,
}: FindClosestPointOptions): FrequencyWaterfallChartSeriesDataPoint | undefined => {
    if (isChartRangeEmpty(leftAxisZoom) || isChartRangeEmpty(bottomAxisZoom)) {
        return undefined;
    }

    const { width, height } = firstSeriesBounds;

    return seriesDataPoints.reduce<
        | {
              distance: number;
              closestPoint: FrequencyWaterfallChartSeriesDataPoint;
          }
        | undefined
    >((result, seriesDataPoint) => {
        const { dataPoint, seriesIndex } = seriesDataPoint;

        const { x: mappedX, y: mappedY } = mapDataPoint({
            width,
            height,
            dataPoint,
            xAxisRange: bottomAxisZoom,
            xAxisScale: bottomAxisScale,
            yAxisRange: leftAxisZoom,
            yAxisScale: leftAxisScale,
        });

        if (mappedX < 0 || mappedY < 0 || mappedX > width || mappedY > height) {
            return result;
        }

        const finalMappedX =
            mappedX + firstSeriesBounds.left + seriesOffsetPx * seriesIndex;

        const finalMappedY =
            mappedY + firstSeriesBounds.top - seriesOffsetPx * seriesIndex;

        const distance = (finalMappedX - point.x) ** 2 + (finalMappedY - point.y) ** 2;

        return result === undefined || distance < result.distance
            ? { distance, closestPoint: seriesDataPoint }
            : result;
    }, undefined)?.closestPoint;
};
